import { Box, Button, Divider, Paper, Stack, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton/IconButton';
import Snackbar from '@mui/material/Snackbar/Snackbar';
import CloseIcon from '@mui/icons-material/Close';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { AuthMechanisms } from '../../Common/Helper/AuthMechanisms';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { EkkoLogo } from 'src/Common/Components/EkkoLogo';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, OpenInNew } from '@mui/icons-material';
import { spacing } from 'src/Common/Helper/ThemeHelper';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { LoadingMask } from 'src/Common/Components/LoadingMask';

export const Login = (): React.JSX.Element => {
  const { isAuthenticated, isAuthenticating, logIn, config } = useEkko();
  const navigate = useNavigate();

  const [isSnackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const isDev = useMemo(() => config.api.authMechanism === AuthMechanisms.JWT, [config]);

  /** handle redirects */
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
      return;
    }
  }, [isAuthenticated]);

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();

      if (!isDev) {
        logIn(undefined, false);
        return;
      }

      try {
        logIn({ username, password }, false);
      } catch (e) {
        setSnackbarOpen(true);
      }
    },
    [isDev, username, password, logIn],
  );

  const form = useMemo(
    () => (
      <Stack spacing={3} direction={'column'}>
        <Stack direction={'column'} spacing={0} alignItems={'center'}>
          <Link to={{ pathname: 'https://www.ekko.io' }} target="_blank">
            <Tooltip
              placement={'top-end'}
              title={
                <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} spacing={1}>
                  <OpenInNew />
                  <Typography>{'ekko.io'}</Typography>
                </Stack>
              }
            >
              <EkkoLogo width={260} height={92} />
            </Tooltip>
          </Link>
        </Stack>
        <Box component={'form'} sx={{ width: '100%', marginTop: (theme) => spacing(theme) }}>
          {!isDev && (
            <Typography component="h1" variant="h5">
              {'Authentication required'}
            </Typography>
          )}
          {isDev && (
            <Stack direction={'column'} spacing={3} alignItems={'stretch'} justifyContent={'flex-start'}>
              <Divider></Divider>
              <StyledTextField
                required
                fullWidth
                autoFocus
                name={'username'}
                label={'Username'}
                value={username}
                onChange={(e) => setUsername(String(e.target.value))}
              />
              <StyledTextField
                required
                fullWidth
                name={'password'}
                label={'Password'}
                type={'password'}
                value={password}
                onChange={(e) => setPassword(String(e.target.value))}
              />

              <Button fullWidth type={'submit'} variant={'contained'} color={'primary'} onClick={onSubmit}>
                Sign in
              </Button>

              <Divider></Divider>

              <Button
                variant={'outlined'}
                rel="noopener noreferrer"
                href={`mailto:support@ekko.io`}
                startIcon={<Mail />}
              >
                <Typography>support@ekko.io</Typography>
              </Button>
            </Stack>
          )}
        </Box>
      </Stack>
    ),
    [username, password, onSubmit],
  );

  if (isAuthenticating) {
    return <LoadingMask />;
  }

  return (
    <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={'80vh'} width={'100vw'}>
      <Paper sx={{ display: 'block', p: 2, px: 5 }}>{form}</Paper>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={isSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        ContentProps={{
          'aria-describedby': 'message-id',
        }}
        message={<span id="message-id">Bad credentials.</span>}
        action={[
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={() => setSnackbarOpen(false)}
            size="large"
            sx={{
              padding: (theme) => spacing(theme) / 2,
            }}
          >
            <CloseIcon />
          </IconButton>,
        ]}
      />
    </Box>
  );
};
